/**
 * Strength and maturity chart helpers for the pour dashboard (Location
 * Details and the Strength Charts tab), ASTM C1074 9.4:
 *  - the tested range: a shaded band over the strengths the calibration
 *    tested, and the span of time this location's maturity was inside it;
 *  - the trend extension: the curve is drawn past the tested range (25 % by
 *    default, the calibration graph's window), faded and dashed there, with
 *    a tooltip and a caption saying it is a trend estimate only;
 *  - achievement markers: one point on the curve where each target was
 *    reached (in place of the old vertical "Achieved" lines);
 *  - target label declutter: target line labels closer than about 16 px are
 *    hidden on each render, pending targets kept first.
 *
 * Pure functions (no React, no API calls). Highcharts is imported for its
 * types only: the test stubs of Highcharts lack its colour helpers.
 */
import type Highcharts from 'highcharts/highstock';
import type { CalibratedRange, ChartTarget, MaturityDescriptor } from '../../services/pours';
import { parseUtc } from '../../utils/datetime';
import type { IsoLike } from '../../utils/datetime';
import {
  TARGET_OUTSIDE_RANGE_LABEL,
  formatHalfUp,
  formatMaturityWithIndex,
  isTargetOutsideRange,
  maturityShortLabel,
  maturityValuePrefix,
  targetDisplayName,
  valueWithUnit,
} from '../../utils/maturityDisplay';
import { elapsedSinceStart } from '../../utils/targetStatus';

export { TREND_ESTIMATE_TOOLTIP } from '../../utils/maturityDisplay';

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** The range has a usable curve with included samples: only then is there a
 *  tested range to show. */
const hasTestedRange = (range?: CalibratedRange | null): range is CalibratedRange =>
  !!range && range.has_curve && range.has_samples;

// ---------------------------------------------------------------------------
// Tested span and trend-extension zones
// ---------------------------------------------------------------------------

/** When this location's maturity was inside the tested range, epoch ms. */
export interface TestedSpan {
  /** When maturity reached min_m; the first reading's time when it started
   *  inside (or past) the range; null when it never reached it (every
   *  drawn point is then a trend estimate). */
  from: number | null;
  /** When maturity first passed max_m; null while it has not. */
  to: number | null;
}

/** The maturity of an ea row ([time, strength|null, maturity]); older
 *  responses sent [time, maturity]. */
const rowMaturity = (row: ReadonlyArray<number | null>): number | null =>
  finite(row.length > 2 ? row[2] : row[1]);

/** The time maturity reaches `level` between two readings (linear). */
const crossing = (t0: number, m0: number, t1: number, m1: number, level: number): number =>
  m1 === m0 ? t1 : t0 + ((level - m0) / (m1 - m0)) * (t1 - t0);

/**
 * The span of time this location's maturity was inside the calibration's
 * tested range [min_m, max_m], interpolated between readings (the same range
 * as the backend's CalibratedRange.evaluate). Null without a usable curve
 * with included samples (no tested range).
 */
export const testedTimeSpan = (
  ea: ReadonlyArray<ReadonlyArray<number | null>> | null | undefined,
  range: CalibratedRange | null | undefined,
): TestedSpan | null => {
  if (!hasTestedRange(range)) return null;
  const lo = finite(range.min_m);
  const hi = finite(range.max_m);
  if (lo == null || hi == null) return null;
  const readings = (ea ?? [])
    .map((row) => [finite(row[0]), rowMaturity(row)] as const)
    .filter((r): r is readonly [number, number] => r[0] != null && r[1] != null)
    .sort((a, b) => a[0] - b[0]);

  let from: number | null = null;
  let to: number | null = null;
  let prev: readonly [number, number] | null = null;
  for (const [t, m] of readings) {
    if (from == null && m >= lo) {
      from = prev && prev[1] < lo ? crossing(prev[0], prev[1], t, m, lo) : t;
    }
    if (from != null && m > hi) {
      to = prev && prev[1] <= hi ? crossing(prev[0], prev[1], t, m, hi) : t;
      break;
    }
    prev = [t, m];
  }
  return { from, to };
};

/** Whether time `x` is outside the tested span, i.e. a trend estimate. False
 *  without a span (no tested range to compare with). */
export const outsideTestedSpan = (x: number, span: TestedSpan | null | undefined): boolean => {
  if (!span) return false;
  if (span.from == null) return true;
  return x < span.from || (span.to != null && x > span.to);
};

/** A hex (#rgb / #rrggbb) or rgb() colour at `alpha` opacity, as rgba();
 *  any other colour is returned unchanged. */
export const fadeColor = (color: string, alpha: number): string => {
  const text = color.trim();
  let rgb: number[] | null = null;
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, (c) => c + c) : hex[1];
    rgb = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  } else {
    const fn = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(text);
    if (fn) rgb = [fn[1], fn[2], fn[3]].map(Number);
  }
  return rgb ? `rgba(${rgb.join(', ')}, ${alpha})` : color;
};

/**
 * Series zones (use with `zoneAxis: 'x'`) that draw the curve faded and
 * dashed outside the tested span, over a faint fill; inside it the series'
 * own colour, line and fill. No zones without a span.
 */
export const extrapolatedZones = (
  span: TestedSpan | null | undefined,
  color: string,
): Highcharts.SeriesZonesOptionsObject[] => {
  if (!span) return [];
  const faded: Highcharts.SeriesZonesOptionsObject = {
    color: fadeColor(color, 0.5),
    fillColor: fadeColor(color, 0.06),
    dashStyle: 'ShortDash',
    className: 'trend-estimate',
  };
  if (span.from == null) return [{ ...faded }];
  if (span.to == null) return [{ value: span.from, ...faded }, {}];
  return [{ value: span.from, ...faded }, { value: span.to }, { ...faded }];
};

/** Fill of the tested-range band: a faint green. */
export const TESTED_BAND_COLOR = 'rgba(46, 125, 50, 0.08)';

/** The strength axis band over the tested range, S(min) to S(max), behind
 *  the curve and without a label (the caption explains it). None without a
 *  tested range. */
export const testedRangeBand = (range: CalibratedRange | null | undefined): Highcharts.YAxisPlotBandsOptions[] => {
  if (!hasTestedRange(range)) return [];
  const lo = finite(range.s_min);
  const hi = finite(range.s_max);
  if (lo == null || hi == null || hi <= lo) return [];
  return [{ id: 'tested-range', from: lo, to: hi, color: TESTED_BAND_COLOR, zIndex: 0 }];
};

/** "TTF 195.5 to 530.1 °C·hrs" / "12.0 to 50.0 hrs of EA at 20 °C": an
 *  equivalent age names its index after the values, so the sentence has no
 *  colon in its middle. */
const maturitySpanText = (lo: number, hi: number, meta: MaturityDescriptor): string =>
  meta.kind === 'ea'
    ? `${formatHalfUp(lo)} to ${formatHalfUp(hi)} ${meta.unit} of ${maturityShortLabel(meta)}`
    : `${maturityValuePrefix(meta)}${formatHalfUp(lo)} to ${formatHalfUp(hi)} ${meta.unit}`;

export interface StrengthCaptionOptions {
  /** A reached target has no marker on the strength chart (reached past the
   *  drawn curve): say where it is marked. */
  offCurveTargets?: boolean;
  /** The maturity chart's name in that sentence; the maturity index label
   *  ("Temperature-Time Factor", "Equivalent Age at 20 °C") by default. */
  maturityChartName?: string;
}

/**
 * The caption under a strength chart, e.g. "Shaded band: the tested range,
 * 1.7 to 14.2 MPa (TTF 195.5 to 530.1 °C·hrs). Dashed line: trend estimate
 * outside that range. The curve is drawn from TTF 146.6 to 662.6 °C·hrs
 * only. Strength readings outside the tested range show as < 1.7 MPa or
 * > 14.2 MPa." Empty without a tested range.
 */
export const strengthRangeCaption = (
  range: CalibratedRange | null | undefined,
  meta: MaturityDescriptor,
  unit?: string | null,
  { offCurveTargets = false, maturityChartName }: StrengthCaptionOptions = {},
): string => {
  if (!hasTestedRange(range)) return '';
  const sMin = finite(range.s_min);
  const sMax = finite(range.s_max);
  if (sMin == null || sMax == null) return '';
  const lo = valueWithUnit(formatHalfUp(sMin), unit);
  const hi = valueWithUnit(formatHalfUp(sMax), unit);
  const mMin = finite(range.min_m);
  const mMax = finite(range.max_m);
  const pMin = finite(range.plot_min_m);
  const pMax = finite(range.plot_max_m);
  const tested = mMin != null && mMax != null ? ` (${maturitySpanText(mMin, mMax, meta)})` : '';
  const parts = [
    `Shaded band: the tested range, ${formatHalfUp(sMin)} to ${hi}${tested}.`,
    'Dashed line: trend estimate outside that range.',
  ];
  if (pMin != null && pMax != null) {
    parts.push(`The curve is drawn from ${maturitySpanText(pMin, pMax, meta)} only.`);
  }
  parts.push(`Strength readings outside the tested range show as < ${lo} or > ${hi}.`);
  if (offCurveTargets) {
    parts.push(
      `Targets reached outside the drawn curve are marked on the ${maturityChartName || meta.index_label} chart.`,
    );
  }
  return parts.join(' ');
};

// ---------------------------------------------------------------------------
// Axis maxima
// ---------------------------------------------------------------------------

/**
 * The strength axis' soft maximum: the higher of S(max) and the highest
 * target strength, plus 5 %, so target lines above the curve stay in view;
 * capped at 1.5 x the higher of S(max) and the data's maximum, so one far-off
 * target (a grade far past the tested range) does not flatten the curve.
 * Undefined when there is nothing to size by.
 */
export const strengthAxisSoftMax = (
  range: CalibratedRange | null | undefined,
  targetStrengths: ReadonlyArray<number | null | undefined>,
  dataMax?: number | null,
): number | undefined => {
  const sMax = hasTestedRange(range) ? finite(range.s_max) ?? 0 : 0;
  const tMax = Math.max(0, ...targetStrengths.map((s) => finite(s) ?? 0));
  const top = Math.max(sMax, tMax);
  if (!(top > 0)) return undefined;
  const cap = Math.max(sMax, finite(dataMax) ?? 0) * 1.5;
  return cap > 0 ? Math.min(top * 1.05, cap) : top * 1.05;
};

/** The maturity axis' soft maximum: the highest target maturity plus 5 %, so
 *  every target line stays in view before maturity reaches it. */
export const maturityAxisSoftMax = (
  targetMaturities: ReadonlyArray<number | null | undefined>,
): number | undefined => {
  const top = Math.max(0, ...targetMaturities.map((m) => finite(m) ?? 0));
  return top > 0 ? top * 1.05 : undefined;
};

// ---------------------------------------------------------------------------
// Achievement markers
// ---------------------------------------------------------------------------

/** Marker fill for a reached target; amber when it is outside the tested
 *  range. */
export const REACHED_MARKER_COLOR = '#2e7d32';
export const REACHED_OUTSIDE_MARKER_COLOR = '#ed6c02';
/** A reading this close to the achievement time gives the marker's height. */
export const NEAREST_READING_MS = 60 * 1000;

const escapeHtml = (text: string): string =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

export interface AchievementOptions {
  /** 'strength': a target reached outside the drawn curve (before its first
   *  or after its last point) gets no marker; 'maturity': a time with no
   *  reading around it sits at the target's maturity. */
  chart: 'strength' | 'maturity';
  /** The company strength unit, for target names. */
  strengthUnit?: string | null;
  /** The pour's maturity naming, for targets without a strength. */
  meta: MaturityDescriptor;
  /** For the outside-range flag when the response did not send one. */
  range?: CalibratedRange | null;
  /** The location's concrete start (EAStart) and monitoring start, for the
   *  elapsed line. */
  concreteStart?: IsoLike;
  monitoringStart?: IsoLike;
}

/** One marker point (a Highcharts point options object). */
export interface AchievementPoint {
  x: number;
  y: number;
  /** The target names, joined with ", " when several were reached at the
   *  same reading. */
  name: string;
  marker: { fillColor: string };
  custom: {
    /** Tooltip lines, HTML-escaped: "Minimum Stripping (10.0 MPa) reached",
     *  "20h 0m after concrete start", "Outside tested range". */
    lines: string[];
    targetIds: string[];
    outsideRange: boolean;
  };
}

/** The series' value at time `x`: the reading at that time, else the
 *  nearest within NEAREST_READING_MS, else interpolated between the readings
 *  around it; null outside the series. */
const valueAt = (series: ReadonlyArray<ReadonlyArray<number | null>>, x: number): number | null => {
  let nearest: [number, number] | null = null;
  let before: [number, number] | null = null;
  let after: [number, number] | null = null;
  for (const point of series) {
    const px = finite(point[0]);
    const py = finite(point[1]);
    if (px == null || py == null) continue;
    if (px === x) return py;
    if (!nearest || Math.abs(px - x) < Math.abs(nearest[0] - x)) nearest = [px, py];
    if (px < x && (!before || px > before[0])) before = [px, py];
    if (px > x && (!after || px < after[0])) after = [px, py];
  }
  if (nearest && Math.abs(nearest[0] - x) <= NEAREST_READING_MS) return nearest[1];
  if (before && after) return before[1] + ((x - before[0]) / (after[0] - before[0])) * (after[1] - before[1]);
  return null;
};

/**
 * One marker per time a target was reached, on `series` (the chart's
 * [time, value] points): x is the achievement time and y the series' value
 * then. Targets reached at the same time share one marker. Targets not
 * achieved, or achieved without a time, get none.
 */
export const achievementPoints = (
  targets: ReadonlyArray<ChartTarget> | null | undefined,
  series: ReadonlyArray<ReadonlyArray<number | null>>,
  { chart, strengthUnit, meta, range, concreteStart, monitoringStart }: AchievementOptions,
): AchievementPoint[] => {
  // The drawn curve's first and last times (a loop: a long pour has too many
  // readings to spread into Math.min).
  let first: number | null = null;
  let last: number | null = null;
  for (const point of series) {
    const t = finite(point[0]);
    if (t == null || finite(point[1]) == null) continue;
    if (first == null || t < first) first = t;
    if (last == null || t > last) last = t;
  }

  const groups = new Map<number, { y: number; name: string; id: string; flagged: boolean }[]>();
  [...(targets ?? [])]
    .sort((a, b) => a.target_ea - b.target_ea || a.target_strength - b.target_strength)
    .forEach((t) => {
      if (!t.achieved) return;
      const x = parseUtc(t.achievement_time).getTime();
      if (Number.isNaN(x)) return;
      if (chart === 'strength' && (first == null || last == null || x < first || x > last)) return;
      const y = valueAt(series, x) ?? (chart === 'maturity' && t.target_ea > 0 ? t.target_ea : null);
      if (y == null) return;
      const name = targetDisplayName(
        t.target_label?.trim() || null,
        t.target_strength,
        strengthUnit,
        formatMaturityWithIndex(t.target_ea, meta),
      );
      const group = groups.get(x) ?? [];
      group.push({ y, name, id: t.target_id, flagged: isTargetOutsideRange(t, range) });
      groups.set(x, group);
    });

  return Array.from(groups.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([x, group]) => {
      const lines = group.map((g) => `${escapeHtml(g.name)} reached`);
      const since = elapsedSinceStart(x, concreteStart, monitoringStart);
      if (since) lines.push(escapeHtml(since));
      const flagged = group.filter((g) => g.flagged);
      if (flagged.length === group.length) {
        lines.push(TARGET_OUTSIDE_RANGE_LABEL);
      } else if (flagged.length > 0) {
        lines.push(`${TARGET_OUTSIDE_RANGE_LABEL}: ${flagged.map((g) => escapeHtml(g.name)).join(', ')}`);
      }
      return {
        x,
        y: group[0].y,
        name: group.map((g) => g.name).join(', '),
        marker: { fillColor: flagged.length > 0 ? REACHED_OUTSIDE_MARKER_COLOR : REACHED_MARKER_COLOR },
        custom: { lines, targetIds: group.map((g) => g.id), outsideRange: flagged.length > 0 },
      };
    });
};

// ---------------------------------------------------------------------------
// Target label declutter
// ---------------------------------------------------------------------------

/** The id of a target's horizontal line, for declutterTargetLabels. */
export const targetPlotLineId = (targetId: string): string => `target-${targetId}`;

/** Label priority of a target line: a pending target (or, on a chart of
 *  several locations, one not every location has reached) ranks above a
 *  reached one. */
export const targetLabelPriority = (pending: boolean): number => (pending ? 1 : 0);

export interface LabelCandidate {
  /** Pixel position of the label's line. */
  y: number;
  /** Higher wins. */
  priority: number;
}

/**
 * Which labels to show so none is closer than `minGap` px to another: taken
 * greedily by priority, then the higher line (smaller pixel y, the higher
 * value). Returns one flag per item, in the items' order.
 */
export const pickVisibleLabels = (items: ReadonlyArray<LabelCandidate>, minGap = 16): boolean[] => {
  const visible = items.map(() => false);
  const kept: number[] = [];
  items
    .map((_, i) => i)
    .sort((a, b) => items[b].priority - items[a].priority || items[a].y - items[b].y || a - b)
    .forEach((i) => {
      const y = items[i].y;
      if (!Number.isFinite(y)) return;
      if (kept.every((k) => Math.abs(items[k].y - y) >= minGap)) {
        kept.push(i);
        visible[i] = true;
      }
    });
  return visible;
};

/** The parts of a Highcharts plot line this module reads. */
interface PlotLineLike {
  options?: { id?: string; value?: number };
  label?: { show: () => unknown; hide: () => unknown };
}

/**
 * Hides target line labels that would overlap (closer than `minGap` px),
 * keeping the higher-priority ones (pickVisibleLabels). Call it from the
 * chart's `events.render`, which runs after every redraw and zoom. Only
 * lines whose id is in `priorityById` (targetPlotLineId) take part; lines
 * outside the axis' current range are left to Highcharts.
 */
export const declutterTargetLabels = (
  chart: Highcharts.Chart,
  priorityById: Readonly<Record<string, number>>,
  minGap = 16,
): void => {
  (chart.yAxis ?? []).forEach((axis) => {
    const lines = (axis as unknown as { plotLinesAndBands?: PlotLineLike[] }).plotLinesAndBands ?? [];
    const min = finite(axis.min) ?? -Infinity;
    const max = finite(axis.max) ?? Infinity;
    const candidates = lines.flatMap((line) => {
      const id = line.options?.id;
      const value = finite(line.options?.value);
      if (!line.label || id == null || value == null) return [];
      if (!Object.prototype.hasOwnProperty.call(priorityById, id)) return [];
      if (value < min || value > max) return [];
      return [{ label: line.label, y: axis.toPixels(value, false), priority: priorityById[id] }];
    });
    const visible = pickVisibleLabels(candidates, minGap);
    candidates.forEach((c, i) => {
      if (visible[i]) c.label.show();
      else c.label.hide();
    });
  });
};
